/**
 * `look` — the packed appearance word that rides `PlayerSnap.look` (protocol v3 §4).
 *
 * One u32 says everything about a body that another client has to draw: which race it is, which
 * Calling it answered, and which of the four visible gear looks it is wearing. It is built by
 * the server from the character row and its equipment and it is **never** read off anything a
 * client sends — a player who could write their own look could wear armour they do not own.
 *
 * ## Why this is its own module rather than a corner of `protocol.ts`
 * Two halves of the word have two owners. The race and Calling bits come from the character row
 * (character creation, Phase 2d); the head/shoulders/chest/weapon bits come from what is
 * equipped (gear, Phase 2c). Both halves are written by different code on the server and read by
 * the crowd renderer on the client, and all three want the bit layout without pulling in the
 * whole codec. `protocol.ts` re-exports every name here, so nothing that already imported
 * `packLook` from `./protocol.js` had to move.
 *
 * The layout is frozen (`docs/PROTOCOL-V3.md` §4):
 *
 * | bits | width | field | range |
 * |---|---|---|---|
 * | 0..2 | 3 | `race` | 0 = unset, 1 = Human, 2 = Hollow |
 * | 3..5 | 3 | `calling` | `CallingId`: 0 none, 1 Vanguard, 2 Warden, 3 Shade, 4 Tinker |
 * | 6..9 | 4 | `head` | 0 = bare, 1..15 |
 * | 10..13 | 4 | `shoulders` | 0 = bare, 1..15 |
 * | 14..17 | 4 | `chest` | 0 = bare, 1..15 |
 * | 18..22 | 5 | `weapon` | 0 = unarmed, 1..31 |
 * | 23..31 | 9 | reserved | written 0 |
 *
 * Every field defaults to 0, which reads as "unset / bare / unarmed", so a look word can be
 * built long before the pieces that fill it exist. That is deliberate: it is what let the wire
 * carry `look` from the day the protocol froze while gear was still being authored.
 */

/**
 * The two playable bodies. Human is the KayKit Adventurers silhouette; Hollow is the Skeletons
 * one — the Choir's walking dead, which is why the Waking screen can offer a second race
 * without a second humanoid being modelled.
 */
export const Race = { None: 0, Human: 1, Hollow: 2 } as const;
export type RaceId = (typeof Race)[keyof typeof Race];

/** The race ids a player may actually choose. Order is the order the Waking screen draws them. */
export const PLAYABLE_RACES: readonly RaceId[] = [Race.Human, Race.Hollow];

/** True for a race a character row is allowed to hold. `Race.None` is not one. */
export function isPlayableRace(v: number): v is RaceId {
  return v === Race.Human || v === Race.Hollow;
}

/**
 * The parts of `PlayerSnap.look`. Every field is server-built from the character row and its
 * equipment — a client never sends any of it — and every one defaults to 0, which reads as
 * "unset / bare / unarmed" so a look word can be built before the pieces that fill it exist.
 */
export interface LookParts {
  /** `Race`. */
  race?: number;
  /** `CallingId`. */
  calling?: number;
  /** Visible armour look ids, 1..15; 0 = bare. */
  head?: number;
  shoulders?: number;
  chest?: number;
  /** Weapon look id, 1..31; 0 = unarmed. */
  weapon?: number;
}

const LOOK_FIELDS: readonly (readonly [keyof LookParts, number, number])[] = [
  ['race', 0, 0b111],
  ['calling', 3, 0b111],
  ['head', 6, 0b1111],
  ['shoulders', 10, 0b1111],
  ['chest', 14, 0b1111],
  ['weapon', 18, 0b11111],
];

/** Pack an appearance into the u32 that rides `PlayerSnap.look`. Bits 23..31 stay 0 (reserved). */
export function packLook(parts: LookParts): number {
  let out = 0;
  for (const [key, shift, mask] of LOOK_FIELDS) {
    out |= ((parts[key] ?? 0) & mask) << shift;
  }
  return out >>> 0;
}

/** The inverse of `packLook`. Every field is present; 0 means bare/unset. */
export function unpackLook(look: number): Required<LookParts> {
  const v = look >>> 0;
  const out: Record<string, number> = {};
  for (const [key, shift, mask] of LOOK_FIELDS) out[key] = (v >>> shift) & mask;
  return out as unknown as Required<LookParts>;
}

/**
 * Replace only the fields named in `parts`, keeping the rest of `look` as it stands.
 *
 * This is the seam between the two owners of the word. Character creation writes
 * `withLook(look, { race, calling })` and equipment writes
 * `withLook(look, { head, shoulders, chest, weapon })`, and neither has to know — or be able to
 * clobber — what the other put there.
 */
export function withLook(look: number, parts: LookParts): number {
  let out = look >>> 0;
  for (const [key, shift, mask] of LOOK_FIELDS) {
    const v = parts[key];
    if (v === undefined) continue;
    out = (out & ~(mask << shift)) | ((v & mask) << shift);
  }
  return out >>> 0;
}

/** Just the race out of a packed word, without allocating the whole `LookParts` object. */
export function raceOf(look: number): number {
  return (look >>> 0) & 0b111;
}

/** Just the Calling out of a packed word. */
export function callingOf(look: number): number {
  return ((look >>> 0) >>> 3) & 0b111;
}
